import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../components/ui/command";
import { cn } from "./utils";

interface SearchableSelectOption {
  label: string;
  value: string;
}

interface SearchableSelectFieldsetProps {
  label: string;
  value: string;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const SearchableSelectFieldset = ({
  label,
  value,
  options,
  placeholder,
  searchPlaceholder,
  error,
  disabled,
  onChange,
}: SearchableSelectFieldsetProps) => {
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="space-y-1">
      <fieldset className="w-full rounded-[0.6rem] border-0 bg-transparent px-3 pb-2 pt-1 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-all focus-within:shadow-[inset_0_0_0_1px_rgba(59,130,246,0.6),0_0_0_3px_rgba(59,130,246,0.25)]">
        <legend className="mb-1 bg-white px-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="ml-1 text-red-500">*</span>
        </legend>
        <div className="relative h-6">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                disabled={disabled}
                className={cn(
                  "absolute inset-0 h-6 min-h-0 w-full justify-between border-0 bg-transparent p-0 text-sm font-normal leading-none shadow-none hover:bg-transparent",
                  !selectedOption && "text-gray-500",
                )}
              >
                <span className="truncate">
                  {selectedOption?.label || placeholder}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[--radix-popover-trigger-width] p-0"
              align="start"
            >
              <Command>
                <CommandInput
                  placeholder={searchPlaceholder}
                  className="!border-0 !outline-none !ring-0 !shadow-none focus:!border-0 focus:!outline-none focus:!ring-0 focus:!shadow-none focus-visible:!border-0 focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-none"
                />
                <CommandEmpty>No option found.</CommandEmpty>
                <CommandGroup className="max-h-64 overflow-y-auto">
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => {
                        onChange(option.value);
                        setOpen(false);
                      }}
                    >
                      {option.label}
                      <Check
                        className={cn(
                          "ml-auto h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </fieldset>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export { SearchableSelectFieldset };
